import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';

import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { RichTextEditor } from '@/components/organisms/RichTextEditor/RichTextEditor';
import type { PortableTextContent } from '@/components/organisms/RichTextEditor/portableText';

export interface CallNoteData {
  title: string;
  content: PortableTextContent;
}

export interface CallNoteEditorPageProps {
  initialTitle?: string;
  initialContent?: PortableTextContent;
  careCircleName?: string;
  isEditing?: boolean;
  isSaving?: boolean;
  onBack?: () => void;
  onSave: (data: CallNoteData) => void;
}

export const CallNoteEditorPage = ({
  initialTitle = '',
  initialContent,
  careCircleName,
  isEditing = false,
  isSaving = false,
  onBack,
  onSave,
}: CallNoteEditorPageProps) => {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState<PortableTextContent>(
    initialContent || [],
  );
  const [titleError, setTitleError] = useState('');

  const handleSave = useCallback(() => {
    if (!title.trim()) {
      setTitleError('Title is required');
      return;
    }
    setTitleError('');
    onSave({ title: title.trim(), content });
  }, [title, content, onSave]);

  const isValid = title.trim().length > 0 && content.length > 0;

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='md'
        headerSlot={
          <PageHeader
            title={isEditing ? 'Edit Call Note' : 'New Call Note'}
            description={careCircleName ? `For: ${careCircleName}` : undefined}
          />
        }
        contentSlot={
          <Stack spacing={3}>
            <TextField
              label='Title'
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (titleError) setTitleError('');
              }}
              placeholder='e.g., Initial Assessment Call'
              error={!!titleError}
              helperText={titleError}
              disabled={isSaving}
              fullWidth
              required
            />

            <Box>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                Notes
              </Typography>
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder='Enter call notes...'
                isDisabled={isSaving}
                minHeight={300}
              />
            </Box>

            <Stack
              direction='row'
              spacing={2}
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              <Button variant='outlined' onClick={onBack} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                variant='contained'
                onClick={handleSave}
                disabled={isSaving || !isValid}
                startIcon={
                  isSaving ? (
                    <CircularProgress size={16} color='inherit' />
                  ) : null
                }
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

CallNoteEditorPage.displayName = 'CallNoteEditorPage';
export default CallNoteEditorPage;
